import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { BiskTransactionType } from '../models/biskTransactionType';
import { BiskTransactionView } from '../models/biskTransactionView';
import BiskService from '../services/bisk.service';
import TricksService from '../services/tricks.service';
import UserService from '../services/user.service';
import { getDateNowUTCISO } from '../services/utils';
import { getAdjustedTiming, QueryTiming } from './useQueryConfig';

// Extended interface with pre-computed display fields
export interface EnrichedBiskTransaction extends BiskTransactionView {
    // Pre-computed display fields
    display_origin_username: string;
    display_origin_profile_pic: string;
    display_recipient_username: string;
    display_object_name: string;
    display_transaction_type: string;
    display_extra_words: string;
    display_has_note: boolean;
}

// Helper to get transaction type display text
const getTransactionTypeDisplay = (type: string): { typeText: string; extraWords: string } => {
    switch (type) {
        case BiskTransactionType.REDEEM:
            return { typeText: 'redeemed', extraWords: '' };
        case BiskTransactionType.SEND:
            return { typeText: 'sent', extraWords: '' };
        case BiskTransactionType.WITHDRAW:
            return { typeText: 'withdrew', extraWords: '' };
        case BiskTransactionType.BUY:
            return { typeText: 'bought', extraWords: '' };
        case BiskTransactionType.AWARD:
            return { typeText: 'received', extraWords: '' };
        case BiskTransactionType.SEND_TO_OBJECT:
            return { typeText: 'sent', extraWords: 'for the' };
        case BiskTransactionType.REDEEM_FOR_OBJECT:
            return { typeText: 'redeemed', extraWords: 'for a' };
        default:
            return { typeText: '', extraWords: '' };
    }
};

// Enrich a single transaction with all display data
const enrichTransaction = async (transaction: BiskTransactionView): Promise<EnrichedBiskTransaction> => {
    const { typeText, extraWords } = getTransactionTypeDisplay(transaction.transaction_type);

    // Start with existing data or defaults
    let display_origin_username = transaction.origin_username || '';
    let display_origin_profile_pic = transaction.origin_profile_pic || '/assets/photos/defaultProPic.png';
    let display_recipient_username = transaction.target_username || '';
    let display_object_name = '';

    // Fetch origin user info if missing
    if (!transaction.origin_username || !transaction.origin_profile_pic) {
        try {
            const user = await UserService.getUserPublicById(transaction.origin_id);
            display_origin_username = user.username;
            if (user.profile?.profile_pic) {
                display_origin_profile_pic = user.profile.profile_pic;
            }
        } catch (e) {
            console.error('Failed to fetch origin user:', e);
        }
    }

    // Fetch recipient user info if needed
    if ((transaction.transaction_type === BiskTransactionType.SEND ||
        transaction.transaction_type === BiskTransactionType.SEND_TO_OBJECT) &&
        !transaction.target_username) {
        try {
            const user = await UserService.getUserPublicById(transaction.target_id);
            display_recipient_username = user.username;
        } catch (e) {
            console.error('Failed to fetch recipient user:', e);
        }
    }

    // Fetch object name if needed
    if (transaction.transaction_type === BiskTransactionType.SEND_TO_OBJECT && transaction.object_id) {
        try {
            const trick = await TricksService.getUserTrick(transaction.object_id, transaction.target_id);
            display_object_name = trick.trick_name;
        } catch (e) {
            console.error('Failed to fetch trick:', e);
        }
    } else if (transaction.transaction_type === BiskTransactionType.REDEEM_FOR_OBJECT && transaction.object_id) {
        try {
            const object = await BiskService.getShopItemById(transaction.object_id);
            display_object_name = object.name;
        } catch (e) {
            console.error('Failed to fetch shop item:', e);
        }
    }

    return {
        ...transaction,
        display_origin_username,
        display_origin_profile_pic,
        display_recipient_username,
        display_object_name,
        display_transaction_type: typeText,
        display_extra_words: extraWords,
        display_has_note: !!(transaction.notes && transaction.notes.trim() !== ''),
    };
};

// Batch enrich transactions - exported for use in other components
export const enrichTransactions = async (transactions: BiskTransactionView[]): Promise<EnrichedBiskTransaction[]> => {
    // Process in parallel for speed
    return Promise.all(transactions.map(enrichTransaction));
};

export const useBiskTransactions = () => {
    const [biskFilter, setBiskFilter] = useState<string>("");
    const numberOfBiskTransactions = 10;
    const queryClient = useQueryClient();

    const fetchBiskTransactions = async ({ pageParam = getDateNowUTCISO() }): Promise<EnrichedBiskTransaction[]> => {
        let transactions: BiskTransactionView[];
        if (biskFilter !== "") {
            transactions = await BiskService.getBiskTransactionsByType(biskFilter, numberOfBiskTransactions, pageParam);
        } else {
            transactions = await BiskService.getBiskTransactionsAll(numberOfBiskTransactions, pageParam);
        }

        // Enrich all transactions before returning
        return enrichTransactions(transactions);
    };

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        status,
        error,
        refetch
    } = useInfiniteQuery({
        queryKey: ['biskTransactions', biskFilter],
        queryFn: fetchBiskTransactions,
        getNextPageParam: (lastPage) => {
            if (lastPage.length < numberOfBiskTransactions) {
                return undefined; // No more pages
            }
            return lastPage[lastPage.length - 1].date;
        },
        initialPageParam: getDateNowUTCISO(),
        ...getAdjustedTiming(QueryTiming.MEDIUM_HIGH_FREQUENCY),
    });

    const biskTransactions: EnrichedBiskTransaction[] = useMemo(() =>
        data?.pages.flatMap(page => page) || [],
        [data]
    );

    const getMoreBiskTransactions = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    const resetBiskTransactions = useCallback(async () => {
        await queryClient.resetQueries({ queryKey: ['biskTransactions', biskFilter] });
        setBiskFilter("");
        await refetch();
    }, [refetch]);

    const handleBiskFilterChange = useCallback(async (newBiskFilter: string) => {
        console.log("Bisk Filter Changed: " + newBiskFilter);
        setBiskFilter(newBiskFilter);
        queryClient.resetQueries({ queryKey: ['biskTransactions', newBiskFilter] });
        await refetch();
    }, [queryClient]);

    return {
        biskTransactions,
        hasNextPage,
        biskTransactionsLoading: isFetching,
        biskFilter,
        getMoreBiskTransactions,
        resetBiskTransactions,
        handleBiskFilterChange,
        isFetchingNextPage,
        status,
        error
    };
};